// FSRS scheduling (ts-fsrs). Cards are stored with ISO date strings (CardRow.state).
import { createEmptyCard, fsrs, generatorParameters, Rating, State, type Card, type Grade, type RecordLogItem, type ReviewLog } from 'ts-fsrs';
import type { CardRow, ReviewRow } from '@valyrio/engine/api';
import type { CardType } from '@valyrio/engine/types';
import { localDate } from '@valyrio/engine/course';
import { RELEARN } from './studyRules';
// Omat virheet: an error card keeps its right answers in a row (engine/src/pedagogy/own-errors.ts)
import { isOwnErrorCardId, rightDaysAfter } from '@valyrio/engine/pedagogy/own-errors';

export { Rating, State };
export type GradeValue = 1 | 2 | 3 | 4;

export const scheduler = fsrs(
  generatorParameters({
    request_retention: 0.9,
    enable_fuzz: true,
    enable_short_term: true,
    maximum_interval: 3650,
  }),
);

/** Stability (days) from which a word counts as steady ("vakaa"). Same threshold as the API's record. */
export const STEADY_DAYS = 21;

export interface StoredCard {
  due: string;
  stability: number;
  difficulty: number;
  elapsed_days: number;
  scheduled_days: number;
  learning_steps: number;
  reps: number;
  lapses: number;
  state: State;
  last_review?: string;
  /**
   * Successive relearning (lib/studyRules.ts RELEARN): the local days (YYYY-MM-DD) with a correct
   * recall (Vaikea or better), at most RELEARN.days. Absent on a card started before the rule
   * (2026-10-07): such a card is left to FSRS alone.
   */
  recall_days?: string[];
  /**
   * An error card only (Omat virheet, item id "virhe:…"): the local days in a row with a right answer
   * (Osasin or Helppo); a miss empties it, and the card retires at OWN_ERROR_RULES.retireDays.
   */
  right_days?: string[];
}

export interface StoredLog {
  rating: Rating;
  state: State;
  due: string;
  stability: number;
  difficulty: number;
  elapsed_days: number;
  last_elapsed_days: number;
  scheduled_days: number;
  learning_steps: number;
  review: string;
  /**
   * Free practice on a card that was not due: recorded, but the card's schedule was left as it
   * was (lib/session.ts isPracticeOnly). A replay of review_log must skip these rows.
   */
  practice?: true;
}

export function toStored(c: Card): StoredCard {
  const out: StoredCard = {
    due: c.due.toISOString(),
    stability: c.stability,
    difficulty: c.difficulty,
    elapsed_days: c.elapsed_days,
    scheduled_days: c.scheduled_days,
    learning_steps: c.learning_steps,
    reps: c.reps,
    lapses: c.lapses,
    state: c.state,
  };
  if (c.last_review) out.last_review = c.last_review.toISOString();
  return out;
}

/** A card that has never been reviewed (an undone first grade the server may already have). */
export function emptyStoredCard(now: Date): StoredCard {
  return toStored(createEmptyCard(now));
}

export function fromStored(s: StoredCard | undefined | null, now = new Date()): Card {
  if (!s || typeof s !== 'object') return createEmptyCard(now);
  const due = new Date(s.due);
  const c: Card = {
    due: Number.isNaN(due.getTime()) ? new Date(now) : due,
    stability: Number(s.stability) || 0,
    difficulty: Number(s.difficulty) || 0,
    elapsed_days: Number(s.elapsed_days) || 0,
    scheduled_days: Number(s.scheduled_days) || 0,
    learning_steps: Number(s.learning_steps) || 0,
    reps: Number(s.reps) || 0,
    lapses: Number(s.lapses) || 0,
    state: (Number(s.state) || 0) as State,
  };
  if (s.last_review) {
    // A device whose clock ran ahead writes last_review in the future; ts-fsrs then refuses the
    // card (negative elapsed time) and the answer could never be revealed. Clamp to now.
    const last = new Date(s.last_review);
    if (!Number.isNaN(last.getTime())) c.last_review = last.getTime() > now.getTime() ? new Date(now) : last;
  }
  return c;
}

function logToStored(l: ReviewLog, practice = false): StoredLog {
  return {
    rating: l.rating,
    state: l.state,
    due: l.due.toISOString(),
    stability: l.stability,
    difficulty: l.difficulty,
    elapsed_days: l.elapsed_days,
    last_elapsed_days: l.last_elapsed_days,
    scheduled_days: l.scheduled_days,
    learning_steps: l.learning_steps,
    review: l.review.toISOString(),
    ...(practice ? { practice: true as const } : {}),
  };
}

export const cardKey = (type: CardType, itemId: string) => `${type}|${itemId}`;

/** One outcome. `practice`: the card stays as it is (free practice on a card not due). */
export type PreviewItem = RecordLogItem & { practice?: true };
export type Preview = Record<GradeValue, PreviewItem>;

function repeatSafely(card: Card, now: Date) {
  try {
    return scheduler.repeat(card, now);
  } catch (err) {
    console.warn('[fsrs] card could not be scheduled as stored; scheduling from now', err);
    try {
      return scheduler.repeat({ ...card, last_review: new Date(now), elapsed_days: 0 }, now);
    } catch {
      return scheduler.repeat(createEmptyCard(now), now);
    }
  }
}

/**
 * The local days with a correct recall after grading `grade` now (lib/studyRules.ts RELEARN); null =
 * a card started before the rule (left to FSRS alone).
 */
export function recallDaysAfter(prior: StoredCard | undefined, grade: GradeValue, now: Date, tz: string): string[] | null {
  const started = !!prior && Number(prior.state) !== State.New && !!prior.reps;
  const days = !started ? [] : Array.isArray(prior.recall_days) ? prior.recall_days : null;
  if (!days || grade < 2) return days;
  const day = localDate(now, tz);
  return days.includes(day) ? days : [...days, day].slice(-RELEARN.days);
}

/** True once a word's card has been recalled correctly at least once (a pre-rule card: once it reached review). */
export function recalledOnce(state: StoredCard | undefined | null): boolean {
  if (!state || Number(state.state) === State.New || !state.reps) return false;
  return Array.isArray(state.recall_days) ? state.recall_days.length > 0 : Number(state.state) === State.Review;
}

/**
 * Successive relearning: until a word's card has correct recalls on RELEARN.days different days
 * (`days`, this grade included), its next due date is at most RELEARN.maxIntervalDays away. Only
 * the due date moves; FSRS's stability and difficulty stay FSRS's own.
 */
export function relearnCap(item: RecordLogItem, days: readonly string[] | null, type: CardType | undefined, now: Date): RecordLogItem {
  if (!days || !type || !RELEARN.types.includes(type) || days.length >= RELEARN.days) return item;
  const max = now.getTime() + RELEARN.maxIntervalDays * 86_400_000;
  if (item.card.due.getTime() <= max) return item;
  return { ...item, card: { ...item.card, due: new Date(max), scheduled_days: RELEARN.maxIntervalDays } };
}

/**
 * All four outcomes, computed once at reveal so the button label equals what is applied.
 * `practiceOnly` (free practice on a card not due, see lib/session.ts isPracticeOnly): Vaikea,
 * Osasin and Helppo leave the card as it is — their label shows its current due date — and only
 * Uudelleen, a real lapse, reschedules it. With `tz` and `type`, a word still in its first days
 * gets the relearning cap (relearnCap).
 */
export function previewGrades(state: StoredCard | undefined, now: Date, opts: { practiceOnly?: boolean; tz?: string; type?: CardType } = {}): Preview {
  const card = fromStored(state, now);
  const rec = repeatSafely(card, now);
  const capped = (g: GradeValue): RecordLogItem =>
    opts.tz ? relearnCap(rec[g as Grade], recallDaysAfter(state, g, now, opts.tz), opts.type, now) : rec[g as Grade];
  const keep = (g: Rating.Hard | Rating.Good | Rating.Easy): PreviewItem =>
    opts.practiceOnly && state ? { card, log: rec[g].log, practice: true } : capped(g as GradeValue);
  return { 1: capped(1), 2: keep(Rating.Hard), 3: keep(Rating.Good), 4: keep(Rating.Easy) };
}

export function intervalMs(item: RecordLogItem, now: Date): number {
  return item.card.due.getTime() - now.getTime();
}

export interface GradeInput {
  existing: CardRow | undefined;
  itemId: string;
  type: CardType;
  grade: GradeValue;
  mode: ReviewRow['mode'];
  now: Date;
  preview?: Preview;
  id: string;
  /** The pace timezone: the local day of a correct recall (successive relearning). */
  tz?: string;
}

/**
 * Apply a grade: the next CardRow (updatedAt = now) and the ReviewRow for the outbox.
 * `practice`: the grade was practice only and `card` is the existing row, unchanged.
 */
export function applyGrade(input: GradeInput): { card: CardRow; review: ReviewRow; practice: boolean } {
  const { existing, now, grade } = input;
  const prior = existing?.state as StoredCard | undefined;
  const raw: PreviewItem = input.preview?.[grade] ?? repeatSafely(fromStored(prior, now), now)[grade as Grade];
  const days = recallDaysAfter(prior, grade, now, input.tz ?? 'Europe/Helsinki');
  // the relearning cap again (a preview made without the timezone, e.g. a notebook grade): a no-op otherwise
  const item: PreviewItem = raw.practice ? raw : relearnCap(raw, days, input.type, now);
  const iso = now.toISOString();
  if (item.practice && existing) {
    return {
      card: existing,
      review: {
        id: input.id,
        itemId: input.itemId,
        type: input.type,
        grade,
        mode: input.mode,
        reviewedAt: iso,
        log: logToStored(item.log, true),
        isNew: false,
      },
      practice: true,
    };
  }
  const stored: StoredCard = days ? { ...toStored(item.card), recall_days: days } : toStored(item.card);
  // an error card counts its right answers in a row (it retires after enough of them)
  if (isOwnErrorCardId(input.itemId)) stored.right_days = rightDaysAfter(prior?.right_days, grade, localDate(now, input.tz ?? 'Europe/Helsinki'));
  const card: CardRow = {
    itemId: input.itemId,
    type: input.type,
    due: item.card.due.toISOString(),
    state: stored,
    introducedAt: existing?.introducedAt ?? iso,
    updatedAt: iso,
  };
  const review: ReviewRow = {
    id: input.id,
    itemId: input.itemId,
    type: input.type,
    grade,
    mode: input.mode,
    reviewedAt: iso,
    log: logToStored(item.log),
    isNew: !existing || (existing.state as StoredCard | undefined)?.state === State.New,
  };
  return { card, review, practice: false };
}

export type MemoryLevel = 'new' | 'settling' | 'steady';

/** uusi / vakiintumassa / vakaa. */
export function memoryLevel(state: StoredCard | undefined | null): MemoryLevel {
  if (!state || state.state === State.New || !state.reps) return 'new';
  return state.stability >= STEADY_DAYS && state.state === State.Review ? 'steady' : 'settling';
}

/** Probability of recall now (0 for unseen cards). */
export function retrievability(state: StoredCard | undefined | null, now: Date): number {
  if (!state || state.state === State.New) return 0;
  return scheduler.get_retrievability(fromStored(state, now), now, false);
}
